import { Bonus, BonusThreshold, BonusThresholdMetric, BonusType } from '@/types/api/bonus';
import { Money } from '@/types/api/utils';

/** Whole kronor, e.g. "10 000 kr". Takes an amount in minor currency unit. */
export const formatKronor = (amount: number, currency = 'SEK') =>
  Intl.NumberFormat('sv-SE', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount / 100);

const formatItems = (count: number) => `${count.toLocaleString('sv-SE')} ${count === 1 ? 'produkt' : 'produkter'}`;

/** "+5 %" or "500 kr". */
export const bonusAmountText = (bonus: Bonus) =>
  bonus.type === BonusType.Rate ? `+${bonus.rate} %` : formatKronor(bonus.amount!.amount, bonus.amount!.currency);

/** "+5 % extra vinst" or "500 kr extra vinst". */
export const bonusValueText = (bonus: Bonus) => `${bonusAmountText(bonus)} extra vinst`;

/** "när ni sålt 50 produkter" or "när ni sålt för 10 000 kr"; "när de sålt …" about another group. */
export const thresholdText = (threshold: BonusThreshold, subject: 'ni' | 'de' = 'ni') =>
  threshold.metric === BonusThresholdMetric.ItemCount
    ? `när ${subject} sålt ${formatItems(threshold.value)}`
    : `när ${subject} sålt för ${formatKronor(threshold.value)}`;

/** "vid 50 st" or "vid 10 000 kr". */
export const thresholdShortText = (threshold: BonusThreshold) =>
  threshold.metric === BonusThresholdMetric.ItemCount
    ? `vid ${threshold.value.toLocaleString('sv-SE')} st`
    : `vid ${formatKronor(threshold.value)}`;

/** "+5 % extra vinst när ni sålt 50 produkter". */
export const bonusText = (bonus: Bonus) =>
  bonus.threshold ? `${bonusValueText(bonus)} ${thresholdText(bonus.threshold)}` : bonusValueText(bonus);

/** How far a sale has come toward a bonus threshold. */
export function thresholdProgress(threshold: BonusThreshold, sale: { unitCount: number; revenue: Money }) {
  const isItems = threshold.metric === BonusThresholdMetric.ItemCount;
  const current = isItems ? sale.unitCount : sale.revenue.amount;
  const left = Math.max(0, threshold.value - current);

  return {
    percent: Math.min(100, (current / threshold.value) * 100),
    /** "12 produkter kvar" or "1 200 kr kvar". */
    leftText: `${isItems ? formatItems(left) : formatKronor(left, sale.revenue.currency)} kvar`,
  };
}
